import { useSessionBlocked, useSessionTransition } from '@/stores/sessionTransition';
import { useSessionRecovery } from '@/stores/sessionRecovery';
import * as React from 'react';
import { Toaster as SonnerToaster, toast } from 'sonner';
import { cn } from '@/lib/utils';

/**
 * Sober DS (v2) — Toast. Thin wrapper over `sonner`.
 *
 * Mapped onto the Sober surface (rounded-so-md, 1px sober-outline, white
 * surface, raised shadow) — no rich colors, no colored borders. Mounted once
 * by the app root; do NOT render a second global Toaster that
 * would conflict with the v1 toaster. Screens call `toast.success/error/info`.
 */
export type SoberToasterProps = React.ComponentProps<typeof SonnerToaster>;

// Covers direct sonner callers as well as the design-system export.
useSessionRecovery.subscribe((state, previous) => {
  if (state.phase !== previous.phase) toast.dismiss();
});
useSessionTransition.subscribe((state, previous) => {
  if (state.isSwitchingTenant !== previous.isSwitchingTenant) toast.dismiss();
});
export const SoberToaster = ({ toastOptions, ...props }: SoberToasterProps) => {
  const paused = useSessionBlocked();
  return (
  <SonnerToaster
    position="top-right"
    richColors={false}
    toastOptions={{
      ...toastOptions,
      classNames: {
        toast: cn(
          'rounded-so-md border border-sober-outline bg-sober-surface text-sober-text shadow-so-raised',
          'text-so-14 font-bold'
        ),
        title: 'text-sober-text',
        description: 'text-sober-secondary font-normal',
        actionButton: 'rounded-so-sm bg-sober-primary-fg text-white',
        cancelButton: 'rounded-so-sm bg-sober-muted text-sober-secondary',
        closeButton: 'border-sober-outline bg-sober-surface text-sober-secondary',
        icon: 'text-sober-secondary',
        success: 'text-sober-green-fg',
        error: 'text-sober-red',
        info: 'text-sober-primary',
        ...toastOptions?.classNames,
      },
    }}
    {...props}
    style={{ ...props.style, ...(paused ? { display: 'none' } : {}) }}
  />
  );
};
SoberToaster.displayName = 'SoberToaster';

export { toast };
